import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider, REGION_SLUGS } from './contexts/LanguageContext';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/FloatingContact';
import { ScrollToTop } from './components/ScrollToTop';
import { AnalyticsTracker } from './components/AnalyticsTracker';
import { ContentProtector } from './components/ContentProtector';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Home } from './pages/Home';
import { About } from './pages/About';
import { Mentor } from './pages/Mentor';
import { Courses } from './pages/Courses';
import { Events } from './pages/Events';
import { Gallery } from './pages/Gallery';
import { Contact } from './pages/Contact';
import { Donate } from './pages/Donate';
import { Login } from './pages/Login';
import { Admin } from './pages/Admin';
import { Terms, Privacy, Refund, Cancellation } from './pages/Legal';
import { Publications } from './pages/Publications';
import DonationThankYou from './pages/DonationThankYou';
import PayBalance from './pages/PayBalance';
import './App.css';

// Same route tree, mounted at both `/` and each region prefix.
const RouteTree = () => (
  <Routes>
    <Route path="/" element={<Home />} />
    <Route path="/about" element={<About />} />
    <Route path="/mentor" element={<Mentor />} />
    <Route path="/courses" element={<Courses />} />
    <Route path="/events" element={<Events />} />
    <Route path="/gallery" element={<Gallery />} />
    <Route path="/publications" element={<Publications />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/donate" element={<Donate />} />
    <Route path="/donate/thank-you" element={<DonationThankYou />} />
    <Route path="/pay-balance/:registrationId" element={<PayBalance />} />
    <Route path="/terms-and-conditions" element={<Terms />} />
    <Route path="/privacy-policy" element={<Privacy />} />
    <Route path="/refund-policy" element={<Refund />} />
    <Route path="/cancellation-policy" element={<Cancellation />} />
    <Route path="/login" element={<Login />} />
    <Route
      path="/admin"
      element={
        <ProtectedRoute requireAdmin>
          <Admin />
        </ProtectedRoute>
      }
    />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

function App() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <LanguageProvider>
          <ScrollToTop />
          <AnalyticsTracker />
          <ContentProtector />
          <div className="App scroll-smooth">
            <Navigation />
            <Toaster position="top-right" richColors />
            <Routes>
              {/* Regional subpaths — each renders the full route tree; LanguageContext auto-detects
                  the region from the URL and applies the correct language + state scope. */}
              {REGION_SLUGS.map((slug) => (
                <Route key={slug} path={`/${slug}/*`} element={<RouteTree />} />
              ))}
              {/* Default (English / no region) tree */}
              <Route path="/*" element={<RouteTree />} />
            </Routes>
            <Footer />
            <FloatingContact />
          </div>
        </LanguageProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
